import Phaser from 'phaser';
import { BeatSync } from './BeatSync';
import { LaserRenderer } from './LaserRenderer';
import { StrobeController } from './StrobeController';
import { DanceFloorAnimator } from './DanceFloorAnimator';
import { FogParticleSystem } from './FogParticleSystem';
import { AmbientOverlay } from './AmbientOverlay';
import { BPM, EquipmentType } from '@/config';
import { EventBus, GameEvent } from '@/utils/EventBus';

interface EquipmentPosition {
  type: EquipmentType;
  col: number;
  row: number;
  worldX: number;
  worldY: number;
  sprite?: Phaser.GameObjects.Image;
}

/**
 * Coordinates all beat-driven visual effects.
 * Subscribes to equipment placement events and updates effect sources.
 */
export class EffectsManager {
  readonly beatSync: BeatSync;
  private laserRenderer: LaserRenderer;
  private strobeController: StrobeController;
  private danceFloorAnimator: DanceFloorAnimator;
  private fogParticleSystem: FogParticleSystem;
  private ambientOverlay: AmbientOverlay;

  constructor(scene: Phaser.Scene) {
    this.beatSync = new BeatSync(BPM);
    this.laserRenderer = new LaserRenderer(scene);
    this.strobeController = new StrobeController(scene);
    this.danceFloorAnimator = new DanceFloorAnimator();
    this.fogParticleSystem = new FogParticleSystem(scene);
    this.ambientOverlay = new AmbientOverlay(scene);

    // Wire up beat callbacks
    this.beatSync.onBeat((count) => {
      this.laserRenderer.onBeat(count);
      this.strobeController.onBeat(count);
      this.danceFloorAnimator.onBeat(count);
      this.fogParticleSystem.onBeat(count);
      this.ambientOverlay.onBeat(count);
      EventBus.emit(GameEvent.BEAT, count);
    });

    // Listen for equipment changes
    EventBus.on(GameEvent.EQUIPMENT_PLACED, () => this.needsRefresh = true);
    EventBus.on(GameEvent.EQUIPMENT_REMOVED, () => this.needsRefresh = true);
  }

  private needsRefresh = true;

  /** Provide the current list of all placed equipment to refresh effect sources */
  refreshSources(equipment: EquipmentPosition[]): void {
    const lasers = equipment
      .filter((e) => e.type === EquipmentType.LASER)
      .map((e) => ({ worldX: e.worldX, worldY: e.worldY }));
    this.laserRenderer.setSources(lasers);

    const strobes = equipment
      .filter((e) => e.type === EquipmentType.STROBE)
      .map((e) => ({ worldX: e.worldX, worldY: e.worldY }));
    this.strobeController.setSources(strobes);

    const fogMachines = equipment
      .filter((e) => e.type === EquipmentType.FOG_MACHINE)
      .map((e) => ({ worldX: e.worldX, worldY: e.worldY }));
    this.fogParticleSystem.setSources(fogMachines);

    const danceFloors = equipment
      .filter((e) => e.type === EquipmentType.DANCE_FLOOR && e.sprite)
      .map((e) => ({ col: e.col, row: e.row, sprite: e.sprite! }));
    this.danceFloorAnimator.setTiles(danceFloors);

    // Update ambient overlay with equipment counts
    this.ambientOverlay.setEquipmentCounts(
      lasers.length,
      fogMachines.length,
      strobes.length
    );

    this.needsRefresh = false;
  }

  /** Call from scene update(). */
  update(deltaMs: number, getEquipment: () => EquipmentPosition[]): void {
    if (this.needsRefresh) {
      this.refreshSources(getEquipment());
    }

    this.beatSync.update(deltaMs);
    this.laserRenderer.update(this.beatSync.beatProgress);
    this.danceFloorAnimator.update(this.beatSync.beatProgress);
    this.ambientOverlay.update(this.beatSync.beatProgress);
  }

  destroy(): void {
    this.laserRenderer.destroy();
    this.strobeController.destroy();
    this.danceFloorAnimator.destroy();
    this.fogParticleSystem.destroy();
    this.ambientOverlay.destroy();
  }
}
